<script setup lang="ts">
import {WidgetProps} from "@/utils/presenters/interfaces.ts";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import EditViewUtils from "@/utils/editView/EditViewUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import EditScreenField from "@/components/EditView/editScreens/EditScreenField.vue";
import Translator from "@/utils/common/Translator.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import {Screen} from "quasar";
import {
  addRefToList,
  commonCanReceiveFocus,
  commonFocus,
  commonNavigateToNext,
  NamaNavigateToNextEmit,
  NamaNavigationArgs,
  NamaVueWidgetContainer
} from "@/utils/common/NamaVueWidget.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";

let props = defineProps<WidgetProps>();
const emit = defineEmits<NamaNavigateToNextEmit>()

function fetchFieldComponent(field: namaTypes.CompositeEditScreenField | namaTypes.EditScreenField | namaTypes.GUIActionPlaceholder) {
  //ToDo: maybe return gui action here?
  return EditScreenField;
}

function isBooleanWidget(field: namaTypes.CompositeEditScreenField | namaTypes.EditScreenField | namaTypes.GUIActionPlaceholder) {
  return ObjectChecker.areEqual(EditViewUtils.getFieldMetaData(props.presenter.fetchMetadata(), field.id)!.fieldType!, FieldType.Boolean)
}

function calcWidgetWidth(field: namaTypes.EditScreenField) {
  if (isBooleanWidget(field)) {
    if (field.hasTitle)
      return field.titlePercentage?.toString() + '%';
    else
      return (field.widgetPercentage! + 10).toString() + '%';
  }
  return field.widgetPercentage?.toString() + '%';
}

let refList: NamaVueWidgetContainer[] = [];

function onRef(widget: any, index: number) {
  refList = addRefToList(refList, {widget, index});
}

function onNavigateToNext(args: NamaNavigationArgs, index: number) {
  commonNavigateToNext(refList, args, index, () => emit('navigateToNext', args));
}

function focus(args: NamaNavigationArgs) {
  commonFocus(refList, args, () => emit('navigateToNext', args));
}

function canReceiveFocus(): boolean {
  return commonCanReceiveFocus(refList);
}

function computeIsFieldDisabled(): boolean {
  const subFields = props.field['subFields'] as namaTypes.EditScreenField[];
  if (!subFields?.length)
    return false;
  return subFields.filter(f => props.presenter.isValidFieldForAttach(f)).every(f => {
    const meta = EditViewUtils.getFieldMetaData(props.presenter.fetchMetadata(), f.id);
    if (!meta)
      return true;
    const util = new FieldWidgetUtil(props.presenter, meta, true);
    return util.isFieldDisabled(props.forceEnableField);
  });
}

function computeIsFieldRequired(): boolean {
  const subFields = props.field['subFields'] as namaTypes.EditScreenField[];
  if (!subFields?.length) return false;
  return subFields.filter(f => props.presenter.isValidFieldForAttach(f)).some(f => {
    const meta = EditViewUtils.getFieldMetaData(props.presenter.fetchMetadata(), f.id);
    if (!meta)
      return false;
    return meta.required === true;
  });
}

defineExpose({focus, canReceiveFocus});
</script>

<template>
  <WidgetContainer
    :field="field"
    :entityType="presenter.entityType"
    :isInsideComposite="isInsideComposite"
    :isFieldDisabled="computeIsFieldDisabled()"
    :isFieldRequired="computeIsFieldRequired()"
    :doNotApplyThemeLayouts="doNotApplyThemeLayouts">
    <template v-slot:widget>
      <div
        :class="[useNamaFlagsStore().showHelpMessagesActive?'tw-items-end':'tw-items-center']"
        class="desktop:tw-flex desktop:tw-flex-row mobile:tw-w-full tw-gap-4 laptop:horizontal:quad:tw-gap-1 desktop:compact:tw-gap-1 desktop:ultra:tw-gap-0.5">
        <template v-for="(field, index) in field['subFields']" :key="index">
          <template v-if="presenter.isValidFieldForAttach(field)">
            <div
              v-if="field.hasTitle && !isBooleanWidget(field)"
              class="tw-self-center tw-text-end"
              :style="{ flexBasis: field.titlePercentage.toString() + '%' }">
              <label :for="field.id" class="tw-text-sm tw-font-light tw-text-gray-700 dark:tw-text-white"
                     v-text="Translator.translateTitledId(presenter.entityType!, field)"/>
            </div>
            <div class="mobile:tw-pb-2 mobile:compact:tw-pb-1 mobile:ultra:tw-pb-1"
                 :style="{ width: Screen.lt.sm ? '100%': (calcWidgetWidth(field)) }">
              <component :is="fetchFieldComponent(field)" :field="field" :isInsideComposite="true" :presenter="presenter"
                         :fieldMetaData="EditViewUtils.getFieldMetaData(presenter.fetchMetadata(), field.id)!"
                         :ref="(r)=>onRef(r,index)" @navigateToNext="(args)=>onNavigateToNext(args,index)"/>
            </div>
          </template>
        </template>
      </div>
    </template>
  </WidgetContainer>
</template>

<style scoped>

</style>
